import type p5 from 'p5'
import { BaseMarkerRenderer } from './baseMarkerRenderer'
import { BaseMarkerController } from './baseMarkerController'
import { BaseMarkerHandles } from './baseMarkerHandles'
import type { Handle } from '../../types'
import { BaseMarkerStateManager } from './baseMarkerStateManager'

abstract class BaseMarker<
  MarkerStateType extends BaseMarkerStateManager<any, any, any>,
  RendererType extends BaseMarkerRenderer<any>,
  ControllerType extends BaseMarkerController<any>,
  HandlesType extends BaseMarkerHandles<any, any>
> {
  p: p5 //TODO replace p with gfx wrapper class around p5
  renderer: RendererType
  controller: ControllerType
  state: MarkerStateType
  handles: HandlesType
  mousedOverHandle: Handle
  //TODO eval moving all these callbacks to a common handler class
  markerSelectedCallback: (marker) => void
  markerDeselectedCallback: (marker) => void
  markerMouseEnterCallback: (marker) => void
  markerMouseLeaveCallback: (marker) => void
  markerCreatedCallback: (marker) => void
  markerEditCallback: (marker) => void

  /**
   * Caculates whether the marker is moused over based on the p5 mouseX and mouseY position
   * @returns boolean
   */
  abstract isMousedOver(): boolean

  /**
   * Calculates the minimum distance from the local mouse x/y position to any of the shapes edges
   */
  //TODO For all markers - the stroke width of the marker isn't taken into account
  //The mouse to shape distance would be more accurate if taking into account the stroke width
  //allowing for a better UX experience when selecting overlapping or nested markers
  abstract getMouseToShapeDistance(): number

  /**
   * String name of the marker - used for saving and restoring state
   */
  public static typeName = 'BaseMarker'

  /**
   * Sets up the stroke for the marker (i.e. Stroke weight, stoke color, etc.)
   */
  setupStroke() {
    const p = this.p
    const color = p.color(this.state.strokeColor)
    const red = p.red(color)
    const green = p.green(color)
    const blue = p.blue(color)
    const alpha = this.state.alpha

    p.strokeWeight(this.state.strokeWeight)
    p.stroke(red, green, blue, alpha)
    p.fill(0, 0, 0, 0) //Transparent black fill
  }

  /**
   * Sets up all items needed to render a full marker (i.e. Stroke, handle coordinates, handle objects, etc.)
   */
  setupMarker() {
    this.handles.populate()
    this.setupStroke()
  }

  /**
   * Sets up the marker and calls it's renderer to draw the marker onto the canvas.
   */
  render(): void {
    this.setupMarker()
    this.renderer.render()
  }

  /**
   * Returns marker instance type name
   */
  public get typeName(): string {
    return Object.getPrototypeOf(this).constructor.typeName
  }
}

export { BaseMarker }
